import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import * as R from 'ramda'
import { Utils } from '@pbt/pbt-ui-components'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import { getIsRetailOrderLineItem } from '~/components/dashboard/invoices/invoiceUtils'
import { OrderType, ProcedureState } from '~/constants/SOAPStates'
import { getChargeSheetSubItemBySoapPatient } from '~/store/duck/clientFinanceData'
import { useIsIntegratedModality } from '~/store/hooks/imagingOrders'
import {
  useGetIsOrderDeletable,
  useGetProcedureStateId,
  useIsFood,
  useIsGlobalInventoryItem,
  useLabTestItemDecline,
} from '~/store/hooks/orders'
import {
  useGetPrescriptionStateType,
  useIsChewyActiveRxReadOnly,
  useIsDeclinableOrDeletable,
} from '~/store/hooks/prescription'
import { getCurrentBusinessIsIdexxImagingEnabled } from '~/store/reducers/auth'
import { getProcedureCategory } from '~/store/reducers/constants'
import type { InvoiceLineItem } from '~/types'
import {
  convertInvoiceLineItemToSoapLog,
  convertRetailOrderLineItemToSoapLog,
} from '~/utils/orderUtils'
import { getPrescriptionType } from '~/utils/prescription'
import useIsCurrentContextItem from '~/utils/useIsCurrentContextItem'

type BasePermissionsParams = {
  disabled: boolean
}

type PrescriptionOrInventoryPermissionsParams = BasePermissionsParams & {
  disabledDeclineToolTip: string
  disabledDeleteToolTip: string
  isChewyActiveRx: boolean
  isChewyReactiveRx?: boolean
  isChewyReadOnly?: boolean
  isContextItem: boolean
  isDeclinableOrDeletable: boolean
  isDeclined: boolean
  isFood: boolean
  isGlobalItem?: boolean
  isInHouse: boolean
  isInventory?: boolean
  isPrepaidAndUsedZero?: boolean
  isSoapFinalized?: boolean
}

type LabtestPermissionsParamas = BasePermissionsParams & {
  disabledDeclineToolTip: string
  disabledDeleteToolTip: string
  isLabTestDeclined: boolean
  isLabTestDeletable: boolean
  isPrepaidAndUsedZero?: boolean
  isSoapFinalized?: boolean
  isUnderOrder: boolean
  labTestDeclineDisabled: boolean
}

type ProcedurePermissionsParams = BasePermissionsParams & {
  disabledDeclineToolTip: string
  disabledDeleteToolTip: string
  isEditable: boolean
  isImaging?: boolean
  isPrepaidAndUsedZero?: boolean
  isProcedureDeclined: boolean
  isSoapFinalized?: boolean
  showImagingEditButton?: boolean
}

const PROCEDURE_CATEGORY = {
  VACCINATION: 'Vaccinations',
  IMAGING: 'Imaging',
}

const getActionButtonsForPrescriptionOrInventoryInvoiceLineItem = ({
  disabled,
  isFood,
  isInHouse,
  isChewyActiveRx,
  isContextItem,
  isChewyReadOnly,
  isGlobalItem,
  isDeclined,
  isInventory,
  disabledDeleteToolTip,
  disabledDeclineToolTip,
  isSoapFinalized,
  isPrepaidAndUsedZero,
}: PrescriptionOrInventoryPermissionsParams) => ({
  Edit: {
    disabled: isSoapFinalized,
    shouldRender:
      !isChewyReadOnly && isGlobalItem ? !isPrepaidAndUsedZero : false,
  },
  EditRX: {
    disabled: isSoapFinalized,
    shouldRender: !isChewyReadOnly && isFood ? !isPrepaidAndUsedZero : false,
  },
  Print: {
    disabled: false,
    shouldRender: !isChewyActiveRx && isContextItem && !isInventory,
  },
  Delete: {
    disabled: disabled || isSoapFinalized,
    tooltip: disabledDeleteToolTip,
    shouldRender: true,
  },
  Decline: {
    disabled: disabled || isSoapFinalized,
    tooltip: disabledDeclineToolTip,
    shouldRender: true,
  },
  Status: {
    disabled: (isDeclined && disabled) || isSoapFinalized,
    shouldRender: !isPrepaidAndUsedZero,
  },
  View: {
    disabled: false,
    shouldRender: isChewyReadOnly,
  },
  Info: {
    disabled: false,
    shouldRender: isInventory || (isGlobalItem ? isInHouse : true),
  },
})

export const getActionButtonsForLabTestItem = ({
  disabled,
  labTestDeclineDisabled,
  isLabTestDeletable,
  isLabTestDeclined,
  disabledDeleteToolTip,
  disabledDeclineToolTip,
  isSoapFinalized,
  isPrepaidAndUsedZero,
}: LabtestPermissionsParamas) => ({
  Delete: {
    disabled: disabled || isSoapFinalized || !isLabTestDeletable,
    tooltip: disabledDeleteToolTip,
    shouldRender: true,
  },
  Decline: {
    disabled:
      disabled || isSoapFinalized || (labTestDeclineDisabled && disabled),
    tooltip: disabledDeclineToolTip,
    shouldRender: true,
  },
  Info: {
    disabled: false,
    shouldRender: true,
  },
  Status: {
    disabled: isSoapFinalized || (isLabTestDeclined && disabled),
    shouldRender: !isPrepaidAndUsedZero,
  },
})

export const getActionButtonsForProcedureItem = ({
  disabled,
  isEditable,
  isProcedureDeclined,
  disabledDeleteToolTip,
  disabledDeclineToolTip,
  isSoapFinalized,
  isPrepaidAndUsedZero,
  showImagingEditButton,
  isImaging,
}: ProcedurePermissionsParams) => ({
  Edit: {
    disabled: isSoapFinalized,
    shouldRender:
      isEditable && !isPrepaidAndUsedZero
        ? isImaging
          ? showImagingEditButton
          : true
        : false,
  },
  Info: {
    disabled: false,
    shouldRender: true,
  },
  Status: {
    disabled: (isProcedureDeclined && disabled) || isSoapFinalized,
    shouldRender: !isPrepaidAndUsedZero,
  },
  Delete: {
    disabled: disabled || isSoapFinalized,
    tooltip: disabledDeleteToolTip,
    shouldRender: true,
  },
  Decline: {
    disabled: disabled || isSoapFinalized,
    tooltip: disabledDeclineToolTip,
    shouldRender: true,
  },
})

export const useChargeInformationPanelPermissions = (
  item: InvoiceLineItem | RetailOrderLineItem,
  isInvoice?: boolean,
  isPrepaidAndUsedZero?: boolean,
) => {
  const { t } = useTranslation(['Tooltips'])

  const disabled = isInvoice || false
  const isRetailOrderLineItem = getIsRetailOrderLineItem(item)

  const patientId = isRetailOrderLineItem ? item.patient.id : item.patientId
  const soapId = isRetailOrderLineItem ? item.soap?.id : item.soapId
  const chargeSheetSectionItem = useSelector(
    getChargeSheetSubItemBySoapPatient(
      patientId,
      soapId,
      isRetailOrderLineItem ? item?.id : item?.chargeSheetSubItemId, // RetailOrderLineItem has no concept of this
    ),
  )
  const isFood = useIsFood(item)
  const isSoapFinalized: boolean = Boolean(
    R.path(['soap', 'finalized'], chargeSheetSectionItem),
  )

  const convertedToOrderItem = isRetailOrderLineItem
    ? convertRetailOrderLineItemToSoapLog(item)
    : convertInvoiceLineItemToSoapLog(item)

  const isGlobalItem = useIsGlobalInventoryItem(convertedToOrderItem)
  const { isInHouse, isChewyActiveRx } = isRetailOrderLineItem
    ? { isInHouse: false, isChewyActiveRx: true }
    : getPrescriptionType(item.prescriptionType, item.origin)
  const isContextItem = useIsCurrentContextItem(convertedToOrderItem)
  const isDeclinableOrDeletable = useIsDeclinableOrDeletable({
    orderType: convertedToOrderItem.type,
    stateId: convertedToOrderItem.stateId,
    prescriptionType: convertedToOrderItem.prescriptionType,
    origin: convertedToOrderItem.origin,
  })
  const { isDeclined } = useGetPrescriptionStateType()(
    isRetailOrderLineItem ? item.logState?.id : item.stateId,
  )

  const getIsDeletable = useGetIsOrderDeletable()
  const {
    declineDisabled: labTestDeclineDisabled,
    isDeclinedState: isLabTestDeclined,
  } = useLabTestItemDecline(convertedToOrderItem, disabled)

  const getProcedureStateId = useGetProcedureStateId()
  const ProcedureCategory = useSelector(getProcedureCategory)
  const isProcedureDeclined = isRetailOrderLineItem
    ? item.declined || false
    : item.stateId === getProcedureStateId(ProcedureState.DECLINED)
  const isIntegratedModality = useIsIntegratedModality(
    convertedToOrderItem?.procedure?.modalityId,
  )
  const isImagingIntegrated = useSelector(
    getCurrentBusinessIsIdexxImagingEnabled,
  )
  const showImagingEditButton = isImagingIntegrated && isIntegratedModality

  const isLabTestDeletable = getIsDeletable(convertedToOrderItem)
  const isUnderOrder = Boolean(
    R.path(['labTest', 'labOrderId'], convertedToOrderItem) ||
    R.prop('labOrderId', convertedToOrderItem),
  )

  const procedureCategoryId = isRetailOrderLineItem
    ? null
    : item.procedureCategoryId
  const isVaccination =
    Utils.findConstantIdByName(
      PROCEDURE_CATEGORY.VACCINATION,
      ProcedureCategory,
    ) === procedureCategoryId

  const isImaging =
    Utils.findConstantIdByName(
      PROCEDURE_CATEGORY.IMAGING,
      ProcedureCategory,
    ) === procedureCategoryId

  const disabledDeleteToolTip = t('Tooltips:DISBALED_DELETE_POSTED_CHARGE')
  const disabledDeclineToolTip = t('Tooltips:DISBALED_DECLINE_POSTED_CHARGE')
  const disabledFinalizedDeleteTooltip = t('Tooltips:DISBALED_DELETE_FINALIZED')
  const disabledFinalizedDeclineTooltip = t(
    'Tooltips:DISBALED_DECLINE_FINALIZED',
  )

  const isChewyReadOnly = useIsChewyActiveRxReadOnly({
    isChewyActiveRx,
    stateId: isRetailOrderLineItem ? item.logState?.id : item.stateId,
    isInvoice,
  })

  const prescriptionPermissions =
    getActionButtonsForPrescriptionOrInventoryInvoiceLineItem({
      isChewyReadOnly,
      disabled,
      isFood,
      isInHouse,
      isChewyActiveRx,
      isContextItem,
      isDeclinableOrDeletable,
      isGlobalItem,
      isDeclined,
      disabledDeleteToolTip: isSoapFinalized
        ? disabledFinalizedDeleteTooltip
        : disabledDeleteToolTip,
      disabledDeclineToolTip: isSoapFinalized
        ? disabledFinalizedDeclineTooltip
        : disabledDeclineToolTip,
      isSoapFinalized,
      isPrepaidAndUsedZero,
    })

  const inventoryPermissions =
    getActionButtonsForPrescriptionOrInventoryInvoiceLineItem({
      disabled,
      isFood,
      isInHouse,
      isChewyActiveRx,
      isContextItem,
      isDeclinableOrDeletable,
      isGlobalItem,
      isDeclined,
      isInventory: true,
      disabledDeleteToolTip: isSoapFinalized
        ? disabledFinalizedDeleteTooltip
        : disabledDeleteToolTip,
      disabledDeclineToolTip: isSoapFinalized
        ? disabledFinalizedDeclineTooltip
        : disabledDeclineToolTip,
      isSoapFinalized,
      isPrepaidAndUsedZero,
    })

  const labTestPermissions = getActionButtonsForLabTestItem({
    disabled,
    isLabTestDeletable,
    labTestDeclineDisabled,
    isLabTestDeclined,
    isUnderOrder,
    disabledDeleteToolTip: isSoapFinalized
      ? disabledFinalizedDeleteTooltip
      : disabledDeleteToolTip,
    disabledDeclineToolTip: isSoapFinalized
      ? disabledFinalizedDeclineTooltip
      : disabledDeclineToolTip,
    isSoapFinalized,
    isPrepaidAndUsedZero,
  })

  const procedurePermission = getActionButtonsForProcedureItem({
    disabled,
    isEditable: isImaging || isVaccination,
    isProcedureDeclined,
    disabledDeleteToolTip: isSoapFinalized
      ? disabledFinalizedDeleteTooltip
      : disabledDeleteToolTip,
    disabledDeclineToolTip: isSoapFinalized
      ? disabledFinalizedDeclineTooltip
      : disabledDeclineToolTip,
    isSoapFinalized,
    isPrepaidAndUsedZero,
    showImagingEditButton,
    isImaging,
  })

  const PermissionsMapLineItem = {
    [OrderType.PRESCRIPTION]: prescriptionPermissions,
    [OrderType.LAB_TEST]: labTestPermissions,
    [OrderType.PROCEDURE]: procedurePermission,
    [OrderType.INVENTORY]: inventoryPermissions,
  }

  return R.propOr(null, R.prop('logType', item), PermissionsMapLineItem)
}
